import { Grid } from '@mui/material';
import Divider from '@mui/material/Divider';
import { useEffect, useState } from 'react';

import { ReactComponent as Save } from 'design_system/src/static/icons/save.svg';
import { Button, Input, Typography } from 'design_system/src';

import useProviders from 'hooks/useProviders';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { SkuStatusEnum } from 'models/sku/interfaces/sku.interface';
import {
	createSkuVariant,
	updateSkuVariant,
} from 'models/sku-variant/controllers/sku-variant.controller';
import {
	CreateSkuVariantDto,
	SkuVariant,
} from 'models/sku-variant/interfaces/sku-variant.interface';
import {
	VehicleColor,
	vehicleColorValues,
} from 'models/inventory/enums/vehicle.enum';

export interface SkuVariantFormValues {
	provider?: string;
	color?: VehicleColor;
	hasGps?: boolean;
	price?: number;
	status?: SkuStatusEnum;
}

const emptyValues: SkuVariantFormValues = {
	provider: '',
	color: undefined,
	hasGps: false,
	price: undefined,
	status: SkuStatusEnum.ACTIVE,
};

const valuesFromVariant = (variant?: SkuVariant | null): SkuVariantFormValues =>
	variant
		? {
				provider: variant.provider ?? '',
				color: variant.color,
				hasGps: variant.hasGps,
				price: variant.price,
				status: variant.status,
		  }
		: emptyValues;

const colorOptions = Object.values(VehicleColor).map((color) => ({
	label: vehicleColorValues[color],
	value: color,
}));

const gpsOptions = [
	{ label: 'Sí', value: 'true' },
	{ label: 'No', value: 'false' },
];

interface SkuVariantDetailsProps {
	skuCode?: string;
	skuId?: string;
	variant?: SkuVariant | null;
	showErrors?: boolean;
	onChange?: (values: SkuVariantFormValues) => void;
	onSaved?: (variant: SkuVariant) => void;
	onClose?: () => void;
}

function SkuVariantDetails({
	skuCode,
	skuId,
	variant,
	showErrors,
	onChange,
	onSaved,
	onClose,
}: SkuVariantDetailsProps) {
	const isModal = !!skuId;
	const [values, setValues] = useState<SkuVariantFormValues>(
		valuesFromVariant(variant),
	);
	const providers = useProviders();
	const [saving, setSaving] = useState(false);
	const [localShowErrors, setLocalShowErrors] = useState(false);

	useEffect(() => {
		setValues(valuesFromVariant(variant));
	}, [variant]);

	useEffect(() => {
		if (!skuId) onChange?.(values);
	}, [values]);

	const displayErrors = showErrors || localShowErrors;
	const priceInvalid = !values.price || values.price <= 0;

	const handleSave = () => {
		if (priceInvalid) {
			setLocalShowErrors(true);
			return;
		}
		if (!skuId) return;

		const dto: CreateSkuVariantDto = {
			provider: values.provider || undefined,
			color: values.color || undefined,
			hasGps: values.hasGps ?? false,
			price: values.price as number,
			status: values.status ?? SkuStatusEnum.ACTIVE,
		};

		setSaving(true);
		const save = variant?._id
			? updateSkuVariant(skuId, variant._id, dto)
			: createSkuVariant(skuId, dto);

		save
			.then((saved) => {
				setSaving(false);
				toasterNotification({
					msg: variant?._id ? 'Variante actualizada' : 'Variante creada',
					toasterType: toasterOptions.success,
					style: 'light',
				});
				onSaved?.(saved);
				onClose?.();
			})
			.catch((err) => {
				setSaving(false);
				toasterNotification({
					msg:
						typeof err?.message === 'string' ? err.message : 'Ocurrió un error',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			});
	};

	const fields = (
		<Grid container direction="row" spacing={3}>
			<Grid item xs={isModal ? 12 : 6}>
				<Input
					title="Proveedor (opcional)"
					placeholder="Selecciona proveedor"
					name="provider"
					type="select"
					options={providers.map((p) => ({ label: p.name, value: p._id }))}
					className="m_b_xs w_100_per"
					value={values.provider ?? ''}
					onChange={(e: any) =>
						setValues({ ...values, provider: e.target.value })
					}
				/>
			</Grid>
			<Grid item xs={isModal ? 12 : 6}>
				<Input
					title="Color principal (opcional)"
					placeholder="Selecciona color"
					name="color"
					type="select"
					options={colorOptions}
					className="m_b_xs w_100_per"
					value={values.color ?? ''}
					onChange={(e: any) => setValues({ ...values, color: e.target.value })}
				/>
			</Grid>
			<Grid item xs={isModal ? 12 : 6}>
				<Input
					title="¿Tiene GPS?"
					name="hasGps"
					type="select"
					options={gpsOptions}
					className="m_b_xs w_100_per"
					value={String(values.hasGps ?? false)}
					onChange={(e: any) =>
						setValues({ ...values, hasGps: e.target.value === 'true' })
					}
				/>
			</Grid>
			<Grid item xs={isModal ? 12 : 6}>
				<Input
					error={displayErrors && priceInvalid}
					subtitle={
						displayErrors && priceInvalid ? 'Campo requerido' : undefined
					}
					title="Precio"
					placeholder="Ej: 5000"
					name="price"
					type="number"
					className="m_b_xs w_100_per"
					value={values.price ?? ''}
					onChange={(e: any) =>
						setValues({ ...values, price: Number(e.target.value) })
					}
				/>
			</Grid>
			{isModal && (
				<Grid item xs={12}>
					<Input
						title="Estado"
						name="status"
						type="select"
						options={[
							{ value: SkuStatusEnum.ACTIVE, label: 'Activo' },
							{ value: SkuStatusEnum.INACTIVE, label: 'Inactivo' },
						]}
						className="m_b_xs w_100_per"
						value={values.status ?? SkuStatusEnum.ACTIVE}
						onChange={(e: any) =>
							setValues({ ...values, status: e.target.value })
						}
					/>
				</Grid>
			)}
		</Grid>
	);

	if (!isModal) {
		return <div className="p_y_sm">{fields}</div>;
	}

	return (
		<div className="dim_100_per flex_content_col">
			<div className="flex_header">
				<Typography
					scale="heading4"
					weight="600"
					textColor="neutral_900"
					className="m_b_lg m_t_md"
				>
					{variant?._id ? 'Editar variante' : 'Añadir variante'}
					{skuCode && (
						<Typography
							scale="large"
							weight="600"
							textColor="primary_300"
							className="m_b_lg"
						>
							{skuCode}
						</Typography>
					)}
				</Typography>
				<Divider />
			</div>
			<div className="flex_body overflow_y_auto p_y_lg p_x_sm">{fields}</div>
			<div className="flex_footer display_flex flex_gap_sm p_md">
				<Button
					variant="outline"
					scale="small"
					className="flex_grow_1"
					onClick={() => onClose?.()}
					disabled={saving}
				>
					Cancelar
				</Button>
				<Button
					scale="small"
					icon={<Save />}
					className="flex_grow_1"
					loading={saving}
					onClick={handleSave}
				>
					Guardar
				</Button>
			</div>
		</div>
	);
}

export default SkuVariantDetails;
